import React, { useState, useEffect } from 'react';
import { adminAPI, getBackendUrl } from '../api';
import { useNavigate } from 'react-router-dom';
import '../App.css';

function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientFirstName, setNewClientFirstName] = useState('');
  const [newClientLastName, setNewClientLastName] = useState('');
  const [newClientPackage, setNewClientPackage] = useState('Starter');
  const [newClientPrice, setNewClientPrice] = useState(300);
  // One-off line items config (fixed prices; onboarding fee amount is editable)
  const ONE_OFF_LINE_ITEMS = [
    { key: 'onboarding_fee', label: 'Onboarding Fee', defaultAmount: 150, editable: true },
    { key: 'software_setup', label: 'Software Set-up', defaultAmount: 250, editable: false },
    { key: 'online_training', label: 'Online Training', defaultAmount: 300, editable: false },
    { key: 'gst_registration', label: 'GST Registration', defaultAmount: 100, editable: false },
    { key: 'hour_pack_3', label: '3hr bookkeeping pack', defaultAmount: 315, editable: false },
    { key: 'hour_pack_5', label: '5hr bookkeeping pack', defaultAmount: 525, editable: false },
    { key: 'hour_pack_10', label: '10hr bookkeeping pack', defaultAmount: 1000, editable: false },
  ];
  const ONE_OFF_PACKAGES = ['Start-up Pack', 'Clean Slate'];
  const isOneOffPackage = (pkg) => ONE_OFF_PACKAGES.includes(pkg);
  // Holds: { onboarding_fee: { included: true, amount: 150 }, software_setup: { included: false, amount: 250 }, ... }
  const initialLineItemState = () => {
    const s = {};
    ONE_OFF_LINE_ITEMS.forEach(it => {
      s[it.key] = { included: false, amount: it.defaultAmount };
    });
    return s;
  };
  const [lineItems, setLineItems] = useState(initialLineItemState());
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(false);
  const [showXeroSection, setShowXeroSection] = useState(false);
  
  // Package pricing mapping
  const packagePricing = {
    'Starter': 300,
    'Essential': 500,
    'Growth': 800,
    'BAS Only': 200,
    'Pro': 1300,
    'Start-up Pack': 0,
    'Clean Slate': 0
  };

  useEffect(() => {
    loadClients();
    loadXeroStatus();
    
    // Check if we just connected Xero
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('xero_connected') === 'true') {
      const tenantName = urlParams.get('tenant');
      setSuccessMessage(`✓ Successfully connected to Xero${tenantName ? ` (${tenantName})` : ''}! Invoice automation is now active.`);
      setShowXeroSection(true); // Expand Xero section to show status
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadClients = async () => {
    try {
      const response = await adminAPI.listClients();
      setClients(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load clients');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadXeroStatus = async () => {
    try {
      const response = await adminAPI.get('/api/xero/status');
      setXeroStatus(response.data);
    } catch (err) {
      console.error('Failed to load Xero status:', err);
    }
  };

  const handleXeroConnect = async () => {
    setXeroLoading(true);
    try {
      const response = await adminAPI.get('/api/xero/connect');
      // Redirect to Xero authorization URL
      window.location.href = response.data.authorization_url;
    } catch (err) {
      setError('Failed to connect to Xero');
      setXeroLoading(false);
    }
  };

  const handleXeroDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Xero? Invoice creation will stop working.')) {
      return;
    }
    
    setXeroLoading(true);
    try {
      // Remove token from database
      await adminAPI.delete('/api/xero/disconnect');
      setSuccessMessage('Xero disconnected successfully');
      loadXeroStatus();
    } catch (err) {
      setError('Failed to disconnect Xero');
    } finally {
      setXeroLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      // Build one_off_items payload from checked items
      const oneOffPayload = ONE_OFF_LINE_ITEMS
        .filter(it => lineItems[it.key]?.included)
        .map(it => ({
          key: it.key,
          label: it.label,
          amount: parseFloat(lineItems[it.key].amount) || 0
        }));
      
      const response = await adminAPI.post('/api/clients', {
        client_email: newClientEmail,
        first_name: newClientFirstName,
        last_name: newClientLastName,
        package: newClientPackage,
        price: parseFloat(newClientPrice) || 0,
        one_off_items: oneOffPayload
      });
      setSuccessMessage(`Onboarding link created! Link: ${response.data.onboarding_url}`);
      setShowCreateModal(false);
      setNewClientEmail('');
      setNewClientFirstName('');
      setNewClientLastName('');
      setNewClientPackage('Starter');
      setNewClientPrice(300);
      setLineItems(initialLineItemState());
      loadClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client session');
    } finally {
      setCreating(false);
    }
  };
  
  const handlePackageChange = (packageName) => {
    setNewClientPackage(packageName);
    setNewClientPrice(packagePricing[packageName]);
    // Reset line items on package change so checkboxes don't carry across
    setLineItems(initialLineItemState());
  };
  
  // Helper: compute total of ticked one-off items
  const computeOneOffTotal = () => {
    return ONE_OFF_LINE_ITEMS
      .filter(it => lineItems[it.key]?.included)
      .reduce((sum, it) => sum + (parseFloat(lineItems[it.key].amount) || 0), 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'created': { class: 'badge-info', text: 'Link Created' },
      'started': { class: 'badge-warning', text: 'In Progress' },
      'signed': { class: 'badge-warning', text: 'Awaiting Verification' },
      'completed': { class: 'badge-success', text: 'Completed' },
      'expired': { class: 'badge-error', text: 'Expired' }
    };
    
    const badge = badges[status] || { class: 'badge-info', text: status };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  // Sort clients: created/started first, then signed, then completed last
  const getSortedClients = () => {
    const statusOrder = { 'created': 1, 'started': 2, 'signed': 3, 'completed': 4, 'expired': 5 };
    return [...clients].sort((a, b) => {
      const orderA = statusOrder[a.status] || 999;
      const orderB = statusOrder[b.status] || 999;
      if (orderA !== orderB) return orderA - orderB;
      // Secondary sort by created date (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  // Filter clients based on search query
  const getFilteredClients = () => {
    const sorted = getSortedClients();
    
    if (!searchQuery.trim()) {
      return sorted;
    }
    
    const query = searchQuery.toLowerCase();
    return sorted.filter(client => 
      (client.business_name?.toLowerCase() || '').includes(query) ||
      (client.client_name?.toLowerCase() || '').includes(query) ||
      (client.client_email?.toLowerCase() || '').includes(query)
    );
  };

  // Calculate KPIs
  const getKPIs = () => {
    const total = clients.length;
    const completed = clients.filter(c => c.status === 'completed').length;
    const inProgress = clients.filter(c => c.status === 'started' || c.status === 'signed').length;
    const pending = clients.filter(c => c.status === 'created').length;
    const verified = clients.filter(c => c.id_verified).length;
    
    return { total, completed, inProgress, pending, verified };
  };

  // Pagination
  const filteredClients = getFilteredClients();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  
  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleDeleteClient = async (sessionId, clientId) => {
    try {
      // Call delete API (we'll need to create this endpoint)
      await fetch(`${getBackendUrl()}/api/clients/${clientId || sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      setSuccessMessage('Client deleted successfully');
      setShowDeleteConfirm(null);
      loadClients();
    } catch (err) {
      setError('Failed to delete client');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-gray-50)' }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'var(--primary-navy)',
        color: 'var(--text-white)',
        padding: '1.5rem 2rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img src="/logo.webp" alt="ST Books" style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
            <h1 style={{ color: 'var(--text-white)', fontSize: '1.5rem' }}>ST Books - Client Dashboard</h1>
          </div>
          <button 
            onClick={onLogout} 
            className="btn btn-outline"
            data-testid="logout-button"
            style={{ borderColor: 'white', color: 'white' }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1400px',
        margin: '2rem auto',
        padding: '0 2rem'
      }}>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {(() => {
            const kpis = getKPIs();
            return (
              <>
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--primary-navy)', color: 'white' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{kpis.total}</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Clients</div>
                </div>
                
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--warning-amber)', color: 'white' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{kpis.inProgress}</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>In Progress</div>
                </div>
                
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--info-blue)', color: 'white' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{kpis.pending}</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Pending Start</div>
                </div>
                
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--success-green)', color: 'white' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{kpis.completed}</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Completed</div>
                </div>
                
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--accent-teal)', color: 'white' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{kpis.verified}</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>ID Verified</div>
                </div>
              </>
            );
          })()}
        </div>
        {successMessage && (
          <div className="alert alert-success" data-testid="success-message">
            {successMessage}
            <button 
              onClick={() => setSuccessMessage('')}
              style={{ float: 'right', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div className="alert alert-error" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Xero Integration Section */}
        <div className="card" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Xero Integration</h3>
              {xeroStatus && (
                xeroStatus.token_stored ? (
                  <span className="badge" style={{ backgroundColor: 'var(--success-green)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem' }}>
                    ✓ Connected
                  </span>
                ) : (
                  <span className="badge" style={{ backgroundColor: 'var(--bg-gray-400)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem' }}>
                    Not Connected
                  </span>
                )
              )}
            </div>
            <button
              className="btn btn-outline"
              onClick={() => setShowXeroSection(!showXeroSection)}
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              {showXeroSection ? '▼ Hide' : '▶ Show'}
            </button>
          </div>

          {showXeroSection && (
            <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--bg-gray-200)' }}>
              {xeroStatus ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-600)', marginBottom: '0.25rem' }}>Status</div>
                      <div style={{ fontWeight: '500' }}>
                        {xeroStatus.token_stored ? (
                          <span style={{ color: 'var(--success-green)' }}>✓ Connected & Ready</span>
                        ) : (
                          <span style={{ color: 'var(--text-gray-600)' }}>Not Connected</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-600)', marginBottom: '0.25rem' }}>Organization</div>
                      <div style={{ fontWeight: '500' }}>
                        {xeroStatus.tenant_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-600)', marginBottom: '0.25rem' }}>Client ID</div>
                      <div style={{ fontWeight: '500', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                        {xeroStatus.client_id_set ? '••••••••' + xeroStatus.redirect_uri.slice(-8) : 'Not Set'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-600)', marginBottom: '0.25rem' }}>Invoice Settings</div>
                      <div style={{ fontSize: '0.875rem' }}>
                        <div>Account: 200 (Sales)</div>
                        <div>Terms: 7 days</div>
                        <div>Tax: GST Exclusive</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'var(--bg-gray-50)', padding: '1rem', borderRadius: '0.375rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-600)', marginBottom: '0.5rem' }}>
                      <strong>How it works:</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-700)' }}>
                      When a client completes onboarding, Xero automatically creates and emails an invoice with the package price.
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    {xeroStatus.token_stored ? (
                      <>
                        <button
                          className="btn btn-outline"
                          onClick={handleXeroDisconnect}
                          disabled={xeroLoading}
                          data-testid="xero-disconnect-button"
                          style={{ 
                            borderColor: 'var(--error-red)', 
                            color: 'var(--error-red)',
                            padding: '0.75rem 1.5rem'
                          }}
                        >
                          {xeroLoading ? 'Disconnecting...' : 'Disconnect Xero'}
                        </button>
                        <button
                          className="btn btn-outline"
                          onClick={loadXeroStatus}
                          style={{ padding: '0.75rem 1.5rem' }}
                        >
                          Refresh Status
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={handleXeroConnect}
                        disabled={xeroLoading}
                        data-testid="xero-connect-button"
                        style={{ padding: '0.75rem 1.5rem' }}
                      >
                        {xeroLoading ? 'Connecting...' : 'Connect to Xero'}
                      </button>
                    )}
                  </div>

                  {!xeroStatus.token_stored && (
                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-yellow-50)', borderLeft: '4px solid var(--warning-amber)', borderRadius: '0.375rem' }}>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-gray-700)' }}>
                        <strong>⚠️ Action Required:</strong> Connect your Xero account to enable automatic invoice creation for completed onboardings.
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="spinner" style={{ margin: '0 auto' }}></div>
                  <p style={{ marginTop: '1rem', color: 'var(--text-gray-600)' }}>Loading Xero status...</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
          <h2>Clients ({filteredClients.length}{searchQuery ? ` of ${clients.length}` : ''})</h2>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: '1', maxWidth: '600px' }}>
            <input
              type="text"
              placeholder="🔍 Search by business name, contact, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              style={{
                flex: 1,
                padding: '0.75rem',
                fontSize: '1rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--bg-gray-200)'
              }}
            />
            {searchQuery && (
              <button
                className="btn btn-outline"
                onClick={() => setSearchQuery('')}
                style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}
              >
                Clear
              </button>
            )}
          </div>
          
          <div style={{display:'flex',gap:'0.75rem',flexWrap:'wrap'}}>
          <button className="btn btn-outline" data-testid="manage-client-agreements-button" onClick={() => navigate('/admin/agreements')}>Client agreements</button>
          <button 
            className="btn btn-primary"
            data-testid="create-client-button"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Onboarding Link
          </button>
          </div>
        </div>

        {/* Clients Table */}
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bg-gray-200)', textAlign: 'left' }}>
                  <th style={{ padding: '1rem' }}>Business Name</th>
                  <th style={{ padding: '1rem' }}>Contact</th>
                  <th style={{ padding: '1rem' }}>Package</th>
                  <th style={{ padding: '1rem' }}>Status</th>
                  <th style={{ padding: '1rem' }}>Progress</th>
                  <th style={{ padding: '1rem' }}>ID Verified</th>
                  <th style={{ padding: '1rem' }}>Created</th>
                  <th style={{ padding: '1rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentClients.map((client) => (
                  <tr 
                    key={client.session_id || client.client_id} 
                    style={{ borderBottom: '1px solid var(--bg-gray-200)' }}
                    data-testid={`client-row-${client.session_id || client.client_id}`}
                  >
                    <td style={{ padding: '1rem' }}>
                      <strong>{client.business_name || 'Not provided'}</strong>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div>{client.client_name}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{client.client_email}</div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600', color: 'var(--primary-navy)' }}>{client.package || 'N/A'}</div>
                      {client.record_type !== 'existing' && <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>${client.price || 0}/mo</div>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {client.record_type === 'existing' ? <span className="badge badge-info" data-testid={`dashboard-agreement-status-${client.client_id}`}>{client.status === 'sent' ? 'Awaiting signature' : client.status === 'signed' ? 'Agreement signed' : client.status}</span> : getStatusBadge(client.status)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {client.record_type === 'existing' ? <span data-testid={`dashboard-agreement-progress-${client.client_id}`}>Agreement only</span> : <><div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${client.progress}%` }}></div>
                      </div>
                      <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{client.progress}%</div></>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {client.record_type === 'existing' ? <span data-testid={`dashboard-existing-id-${client.client_id}`}>Not requested</span> : client.id_verified ? (
                        <span className="badge badge-success">✓ Verified</span>
                      ) : (
                        <span className="badge badge-warning">Pending</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {new Date(client.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {client.status !== 'completed' && client.token && (
                          <button 
                            className="btn btn-primary"
                            data-testid={`copy-link-button-${client.session_id}`}
                            onClick={() => {
                              const link = `${window.location.origin}/onboarding/${client.token}`;
                              navigator.clipboard.writeText(link);
                              setSuccessMessage(`Link copied! ${link}`);
                            }}
                            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          >
                            📋 Copy Link
                          </button>
                        )}
                        <button 
                          className="btn btn-secondary"
                          data-testid={`view-button-${client.session_id || client.client_id}`}
                          onClick={() => navigate(client.record_type === 'existing' ? `/admin/agreements/${client.client_id}` : `/admin/clients/${client.client_id || client.session_id}`)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          disabled={!client.client_id}
                        >
                          View
                        </button>
                        <button 
                          className="btn btn-outline"
                          data-testid={`delete-button-${client.session_id || client.client_id}`}
                          onClick={() => setShowDeleteConfirm(client)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', borderColor: 'var(--error-red)', color: 'var(--error-red)' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredClients.length === 0 && !searchQuery && (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <p>No clients yet. Create your first onboarding link to get started.</p>
              </div>
            )}
            
            {filteredClients.length === 0 && searchQuery && (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <p>No clients found matching "{searchQuery}"</p>
                <button 
                  className="btn btn-outline"
                  onClick={() => setSearchQuery('')}
                  style={{ marginTop: '1rem' }}
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              padding: '1rem', 
              borderTop: '1px solid var(--bg-gray-200)', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredClients.length)} of {filteredClients.length} clients
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  ← Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    className={currentPage === index + 1 ? 'btn btn-primary' : 'btn btn-outline'}
                    onClick={() => setCurrentPage(index + 1)}
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', minWidth: '40px' }}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  className="btn btn-outline"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          zIndex: 1000
        }}>
          <div className="card" style={{
            maxWidth: '500px',
            width: '100%',
            padding: '2rem'
          }}>
            <h3 style={{ marginBottom: '1rem', color: 'var(--error-red)' }}>⚠️ Confirm Deletion</h3>
            <p>Are you sure you want to delete this client?</p>
            <p style={{ marginTop: '1rem' }}>
              <strong>{showDeleteConfirm.business_name || 'Not provided'}</strong><br />
              {showDeleteConfirm.client_name} ({showDeleteConfirm.client_email})
            </p>
            <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              This will permanently delete all associated data including signed documents, uploaded IDs, and audit logs.
            </p>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => setShowDeleteConfirm(null)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn"
                onClick={() => handleDeleteClient(showDeleteConfirm.session_id, showDeleteConfirm.client_id)}
                style={{ flex: 1, backgroundColor: 'var(--error-red)', color: 'white' }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          zIndex: 1000
        }}>
          <div className="card" style={{
            maxWidth: '500px',
            width: '100%',
            padding: '2rem'
          }}>
            <h3>Create Onboarding Link</h3>
            <form onSubmit={handleCreateClient} data-testid="create-client-form">
              <div className="form-group">
                <label htmlFor="clientFirstName">First Name *</label>
                <input
                  type="text"
                  id="clientFirstName"
                  data-testid="client-first-name-input"
                  value={newClientFirstName}
                  onChange={(e) => setNewClientFirstName(e.target.value)}
                  required
                  placeholder="John"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="clientLastName">Last Name *</label>
                <input
                  type="text"
                  id="clientLastName"
                  data-testid="client-last-name-input"
                  value={newClientLastName}
                  onChange={(e) => setNewClientLastName(e.target.value)}
                  required
                  placeholder="Smith"
                />
              </div>

              <div className="form-group">
                <label htmlFor="clientEmail">Client Email *</label>
                <input
                  type="email"
                  id="clientEmail"
                  data-testid="client-email-input"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  required
                  placeholder="john@example.com"
                />
                <p className="help-text">The onboarding link will be sent to this email</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="clientPackage">Package *</label>
                <select
                  id="clientPackage"
                  data-testid="client-package-select"
                  value={newClientPackage}
                  onChange={(e) => handlePackageChange(e.target.value)}
                  required
                >
                  <option value="Starter">Starter - Monthly Bookkeeping</option>
                  <option value="Essential">Essential - Monthly + Payroll</option>
                  <option value="Growth">Growth - Weekly Bookkeeping</option>
                  <option value="BAS Only">BAS Only - BAS Lodgement Services</option>
                  <option value="Pro">Pro - Daily/Advanced Services</option>
                  <option value="Start-up Pack">Start-up Pack - One-off Setup Bundle</option>
                  <option value="Clean Slate">Clean Slate - One-off Catch-up Bookkeeping</option>
                </select>
              </div>
              
              {!isOneOffPackage(newClientPackage) && (
                <div className="form-group">
                  <label htmlFor="clientPrice">Monthly Price (AUD, excl. GST) *</label>
                  <input
                    type="number"
                    id="clientPrice"
                    data-testid="client-price-input"
                    value={newClientPrice}
                    onChange={(e) => setNewClientPrice(e.target.value)}
                    required
                    min="0"
                    step="0.01"
                    placeholder="300"
                  />
                  <p className="help-text">Auto-filled based on package, but can be customized</p>
                </div>
              )}
              
              {/* Add-on / one-off line items (always available; mandatory for one-off packages) */}
              <div className="form-group" data-testid="line-items-section">
                <label style={{ marginBottom: '0.5rem', display: 'block' }}>
                  {isOneOffPackage(newClientPackage)
                    ? 'One-Off Services to Include *'
                    : 'Optional One-Off Add-ons'}
                </label>
                <p className="help-text" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                  All prices exclude GST. {isOneOffPackage(newClientPackage)
                    ? 'Tick the services this client requires.'
                    : 'Tick "Onboarding Fee" to bill a one-off setup charge alongside the monthly fee.'}
                </p>
                <div style={{
                  border: '1px solid var(--bg-gray-200)',
                  borderRadius: '0.375rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--bg-gray-50)'
                }}>
                  {ONE_OFF_LINE_ITEMS
                    .filter(it => isOneOffPackage(newClientPackage) || it.key === 'onboarding_fee')
                    .map(it => (
                      <div key={it.key} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.4rem 0',
                        borderBottom: '1px solid var(--bg-gray-100)'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          margin: 0,
                          cursor: 'pointer',
                          flex: 1
                        }}>
                          <input
                            type="checkbox"
                            data-testid={`lineitem-${it.key}-checkbox`}
                            checked={lineItems[it.key]?.included || false}
                            onChange={(e) => setLineItems({
                              ...lineItems,
                              [it.key]: { ...lineItems[it.key], included: e.target.checked }
                            })}
                          />
                          <span>{it.label}</span>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ color: 'var(--text-muted)' }}>$</span>
                          {it.editable ? (
                            <input
                              type="number"
                              data-testid={`lineitem-${it.key}-amount`}
                              value={lineItems[it.key]?.amount ?? it.defaultAmount}
                              onChange={(e) => setLineItems({
                                ...lineItems,
                                [it.key]: {
                                  ...lineItems[it.key],
                                  amount: e.target.value
                                }
                              })}
                              min="0"
                              step="0.01"
                              disabled={!lineItems[it.key]?.included}
                              style={{ width: '90px', textAlign: 'right', padding: '0.25rem 0.5rem' }}
                            />
                          ) : (
                            <span style={{ fontWeight: 600, minWidth: '60px', textAlign: 'right' }}>
                              {it.defaultAmount.toFixed(2)}
                            </span>
                          )}
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>+ GST</span>
                        </div>
                      </div>
                    ))}
                  
                  {/* Total for one-off packages */}
                  {isOneOffPackage(newClientPackage) && (
                    <div style={{
                      marginTop: '0.75rem',
                      paddingTop: '0.75rem',
                      borderTop: '2px solid var(--primary-navy)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontWeight: 'bold'
                    }} data-testid="one-off-total">
                      <span>One-Off Total</span>
                      <span style={{ color: 'var(--primary-navy)' }}>
                        ${computeOneOffTotal().toFixed(2)} + GST
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button 
                  type="button" 
                  className="btn btn-outline"
                  data-testid="cancel-button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewClientEmail('');
                    setNewClientFirstName('');
                    setNewClientLastName('');
                    setNewClientPackage('Starter');
                    setNewClientPrice(300);
                    setLineItems(initialLineItemState());
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  data-testid="submit-create-button"
                  disabled={creating}
                  style={{ flex: 1 }}
                >
                  {creating ? 'Creating...' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;